import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Content from 'studio/components/Content/Content';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import styles from './DbtSync.styles';
import { DbtIcon } from 'components/Icons';
import TextField from 'studio/components/TextField';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import { getErrorMessage } from 'utils/forms';
import * as yup from 'yup';
import Button from '@mui/material/Button';
import services from 'services';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import Typography from '@mui/material/Typography';
import { pluralize as plrl } from 'studio/helpers';
import SyncIcon from '@mui/icons-material/SyncRounded';
import { trackEvent } from 'utils/eventTracking';
import PageTitle from 'studio/components/PageTitle';

// the free version of data grid limits the number of rows per page to at most 100
const pageSize = 100;

const conceptColumns = [
  { field: 'uri', headerName: 'Uri', width: 200 },
  { field: 'name', headerName: 'Name', width: 200 },
  { field: 'currentDescription', headerName: 'Current Description', width: 350 },
  { field: 'newDescription', headerName: 'New Description', width: 350 }
];

// Validate non-emptiness of the form when submitting
const validationRules = () => {
  return yup.object().shape({
    jobId: yup.string().required('dbt Job Id is required')
  });
};

// In the future we could get the job id from the backend if the functionality was used already once
const defaultValues = {
  jobId: ''
};

const DbtSync = ({ classes, knowledgeGraph }) => {
  useEffect(() => {
    trackEvent('Studio dbt Cloud Sync Opened');
  }, []);

  // the rows of the descriptions table
  const [descriptions, setDescriptions] = useState(null);
  // the ids i.e. uris of the descriptions that are selected in the descriptions table
  const [selectedDescriptionIds, setSelectedDescriptionIds] = useState([]);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();

  const { handleSubmit, control, errors } = useForm({
    defaultValues,
    resolver: yupResolver(validationRules())
  });

  // the rows of the descriptions table
  const descriptionRows = useMemo(
    () =>
      Array.isArray(descriptions) &&
      descriptions.map(description => ({
        id: description.uri, // id is mandatory for the material ui table
        uri: description.uri,
        name: description.name,
        currentDescription: description.currentDescription,
        newDescription: description.newDescription
      })),
    [descriptions]
  );

  const getDescriptions = useCallback(
    async data => {
      const result = await withLoader(services.getDbtCloudDescriptions(knowledgeGraph?.data?.id, data?.jobId));
      if (result.success) {
        const message = `The descriptions have been successfully fetched!`;
        setDescriptions(result.data);
        successMessage(message);
        return;
      }
      errorMessage(result?.error?.message || 'There was an error fetching the descriptions.');
    },
    [withLoader, successMessage, errorMessage]
  );

  const updateDescriptions = useCallback(
    async (descriptions, selectedDescriptionIds) => {
      // Only send the selected rows i.e. descriptions back to the backend
      const body = descriptions.filter(d => selectedDescriptionIds.some(id => d.uri === id));
      const result = await withLoader(services.updateDbtCloudDescriptions(knowledgeGraph?.data?.id, body));
      if (result.success) {
        const message = `The descriptions have been successfully updated!`;
        successMessage(message);
        return;
      }
      errorMessage(result?.error?.message || 'There was an error updating the descriptions.');
    },
    [withLoader, selectedDescriptionIds, descriptions, successMessage, errorMessage]
  );

  return (
    <Content>
      <PageTitle icon={DbtIcon}>dbt Cloud Sync</PageTitle>
      <Typography variant="body1" gutterBottom>
        Choose which dbt Cloud Job ID you want to sync your descriptions from. Make sure that your job has a &apos;
        <i>dbt generate docs</i>&apos; step, otherwise you will not find descriptions for your columns.
      </Typography>
      <form onSubmit={handleSubmit(getDescriptions)} className={classes.jobIdForm}>
        <Controller
          as={TextField}
          className={classes.jobId}
          name="jobId"
          control={control}
          label="dbt Cloud Job ID"
          error={Boolean(errors.jobId)}
          description={getErrorMessage(errors.jobId)}
        />
        <Button type="submit" variant="contained" color="primary" disabled={loading}>
          Get Descriptions
        </Button>
      </form>
      <Typography variant="h2" gutterBottom>
        Descriptions
      </Typography>
      <form>
        <DataGrid
          className={classes.descriptionTable}
          pageSize={pageSize}
          rows={descriptionRows || []}
          columns={conceptColumns.map(column => ({ ...column, disableClickEventBubbling: true }))}
          onSelectionModelChange={newSelection => setSelectedDescriptionIds(newSelection)}
          checkboxSelection
          actions={[
            {
              label: 'Update',
              icon: <SyncIcon />,
              tooltip:
                selectedDescriptionIds.length > 0
                  ? `Update the selected ${plrl(selectedDescriptionIds.length, 'description')}`
                  : 'Selected one or more descriptions to update',
              disabled: loading || selectedDescriptionIds.length === 0,
              onClick: () => updateDescriptions(descriptions, selectedDescriptionIds)
            }
          ]}
          noRowsText="There are no descriptions"
        />
      </form>
    </Content>
  );
};

export default withStyles(styles)(DbtSync);
